import { getMenuAuth } from "src/apis/authApi";
import {  useCallback, useEffect } from "react";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import useMenuGlobalStore from "src/store/useMenuStore";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";

import { usePRD3050_R01Store, defaultPRD3050_R01Filter, PRD3050_R01Filter } from "./Store_PRD3050_R01";
import { PRD3050_R01Dto } from "./Dto_PRD3050_R01";
import { fetchDataSetHandler, fetchFilterConfig } from "src/utils/grid/GridHandlerContent";
import { exportPRD3050_R01Excel, getPRD3050_R01AllCount, getPRD3050_R01List } from "./Api_PRD3050_R01";
import { formatDate } from "src/utils/string/stringUtil";

interface ProductResultContentProps {
    setMessage: (message: string) => void;
}

export const Content_PRD3050_R01 = (props: ProductResultContentProps) => {
    const { setMessage } = props;

    const { 
        PRD3050_R01Filter, setPRD3050_R01Filter,
        PRD3050_R01PastFilter, setPRD3050_R01PastFilter,
        PRD3050_R01List, setPRD3050_R01List, 
        setAllCount,
        setScrollTop,
    } = usePRD3050_R01Store();
    const { setLoadingSpinner } = useLoadingSpinnerStore();
    const setAuth = useActionbarAuthStore((state) => state.setAuth);

    useFetchActionbarAuth("PRD3050_R01");

    useEffect(() => {
        getMenuAuth("PRD3050_R01").then((res) => {
            setAuth(res.data);
        });
    }, []);

    const getParams = (filter: Partial<PRD3050_R01Filter>) => {
		return {
			compCd: filter.compCd,
			yearMonth: filter.yearMonth,
			workplaceCd: filter.workplaceCd,
			jobTeam: filter.jobTeam,
			dateRange: filter.dateRange,
			startDate: filter.dateRange ? formatDate(filter.dateRange.from) : undefined,
			endDate: filter.dateRange ? formatDate(filter.dateRange.to) : undefined,
			closeTag: filter.closeTag,
			workEmp: filter.workEmp,
			workEmpNm: filter.workEmpNm,
		}
	}

    const renewalAllCount = useCallback(async () => {
        const response = await getPRD3050_R01AllCount(getParams(PRD3050_R01Filter));
        setAllCount(response.data.data);
        return response.data.data;
    }, [PRD3050_R01Filter, setAllCount]);

    const fetchData = useCallback(async () => {
        setScrollTop(null);
        setLoadingSpinner(true);
        try {
			
            const response = await getPRD3050_R01List(
                '',
                getParams(PRD3050_R01Filter),
                PRD3050_R01List.length ?? 0
            );

            const rawData = (response.data.data as Omit<PRD3050_R01Dto, 'valueIndex'|'orderIndex'|'orderIndexSeq'>[]).map((row, idx) => ({
                valueIndex: idx,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
                ...row,
            }));

			const newList = [...rawData ] as PRD3050_R01Dto[];
            const processAllCount = await renewalAllCount();

            if (newList && newList.length > 0) {
                setPRD3050_R01PastFilter(PRD3050_R01Filter);
                setPRD3050_R01List(newList);
                ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				
            } else {
                showWarning('데이터가 없습니다.');
                setPRD3050_R01List([]);
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setPRD3050_R01List([]);
        } finally {
            setLoadingSpinner(false);
        }
		setTimeout(() => { setMessage(''); }, 5000);
    }, [PRD3050_R01Filter, setPRD3050_R01List, setPRD3050_R01PastFilter, setScrollTop]);
    
    const handleExcelClick = async () => {
        const {isDefaultFilter, isDefaultPastFilter, listLength} = fetchFilterConfig(
            PRD3050_R01Filter,
            PRD3050_R01PastFilter,
            defaultPRD3050_R01Filter,
            PRD3050_R01List.length
        );

        const response = await exportPRD3050_R01Excel(
            isDefaultFilter? '' : 'search',
            getParams(PRD3050_R01Filter),
            listLength
        );
        downloadSettingExcel({
            responseData: response.data.data,
            fileName: '공정지시 조회-excel.xlsx',
        });
    };

    return {
        fetchData,
        handleExcelClick,
    };
};


